import { Link } from "react-router-dom";
import {
  ArrowDownIcon,
  ArrowOutwardIcon,
  ArrowRightIcon,
} from "../../../../../components/icons";
import { cn } from "../../../../../utils/classnames";

/** Arrow per destination, so the guest can tell where a card goes. */
const ArrowForDestination = ({ destination, color }) => {
  if (destination === "section") {
    // Jumps to a section further down this page.
    return <ArrowDownIcon color={color} size={16} />;
  }
  if (destination === "external") {
    // Leaves for a partner site.
    return <ArrowOutwardIcon color={color} size={14} />;
  }
  // Goes to another page on our site.
  return <ArrowRightIcon color={color} size={16} />;
};

/**
 * The three card shapes in the design:
 *   "centered" — icon, label and arrow grouped in the middle (Explore Transfer Services)
 *   "stacked"  — icon on its own line, label and arrow below (pick-up points)
 *   "inline"   — icon and label on the left, arrow pushed right (private tour links)
 */
// Figma draws the 1px border inside the card, so each padding is 1px less
// than the design value to keep the same outer size.
const LAYOUTS = {
  centered: {
    cardClass:
      "tw:items-center tw:justify-center tw:gap-x-3 tw:px-[19px] tw:py-[23px] tw:lg:py-[31px] tw:border-[#F1F1F1]",
    arrowColor: "#C9C9C9",
  },
  stacked: {
    cardClass: "tw:flex-col tw:gap-y-4 tw:p-[23px] tw:border-[#E7E2D8]",
    arrowColor: "#E7E2D8",
  },
  inline: {
    cardClass: "tw:items-center tw:gap-x-3 tw:p-[23px] tw:border-[#E7E2D8]",
    arrowColor: "#E7E2D8",
  },
};

/**
 * White card that sends the guest somewhere: to a section of this page
 * (`onClick`), to another page (`to`), or to a partner site (`href`).
 * Exactly one of the three is expected.
 */
export const QuickLinkCard = ({
  icon,
  label,
  destination,
  layout = "inline",
  to,
  href,
  onClick,
}) => {
  const { cardClass, arrowColor } = LAYOUTS[layout];

  const wrapperClass = cn(
    // `tw:h-full` keeps cards in the same row equal height once they are
    // wrapped for the stagger animation; the lift is the hover feedback.
    "tw:bg-white tw:border tw:rounded-[5px] tw:flex tw:h-full tw:w-full tw:text-left tw:!no-underline tw:transition tw:duration-200 tw:hover:border-[#D4B055] tw:hover:-translate-y-[2px] tw:hover:shadow-[0_6px_18px_rgba(0,0,0,0.06)]",
    cardClass
  );

  const labelElement = (
    <span className="tw:font-hanken-grotesk tw:font-bold tw:text-[15px] tw:leading-[22px] tw:!text-[#4E4E4E]">
      {label}
    </span>
  );
  const arrowElement = (
    <span className="tw:flex tw:shrink-0">
      <ArrowForDestination destination={destination} color={arrowColor} />
    </span>
  );

  let content;
  if (layout === "stacked") {
    content = (
      <>
        <span className="tw:flex">{icon}</span>
        <span className="tw:flex tw:items-end tw:justify-between tw:gap-x-3">
          {labelElement}
          {arrowElement}
        </span>
      </>
    );
  } else if (layout === "centered") {
    content = (
      <>
        <span className="tw:flex tw:items-center tw:gap-x-2">
          {icon}
          {labelElement}
        </span>
        {arrowElement}
      </>
    );
  } else {
    content = (
      <>
        <span className="tw:flex tw:flex-1 tw:items-center tw:gap-x-3">
          {icon}
          {labelElement}
        </span>
        {arrowElement}
      </>
    );
  }

  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={wrapperClass}>
        {content}
      </a>
    );
  }

  if (to) {
    return (
      <Link to={to} className={wrapperClass}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(wrapperClass, "tw:cursor-pointer")}
    >
      {content}
    </button>
  );
};
